/**
 * EvidenceSheet — single source-of-truth detail panel for any
 * Suggestion. One component, two layers:
 *
 *   • Parent layer is always visible — verdict sentence, video
 *     evidence grid, "What changes if you apply" preview.
 *   • Admin layer is collapsible, persona-gated. Renders the scoring
 *     chain, model version, confidence, and a feedback affordance.
 *
 * Pure presentational. The caller wires data + actions. On desktop
 * the caller provides the docked aside container and EvidenceSheet
 * renders inline; on mobile the component wraps itself in a
 * fullScreen Dialog so the parent page doesn't need to handle the
 * overlay separately.
 *
 * Translated from design/components/EvidenceSheet.jsx. Keep the
 * structure aligned so a design refresh diffs cleanly.
 */
import React, {useState} from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlined from '@mui/icons-material/TrendingDownOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import {
  Evidence,
  EvidenceFactor,
  EvidenceScoring,
  EvidenceVideo,
  PreviewKind,
  SuggestionVerb,
  ThresholdDirection,
} from '../../../types/generated/graphql';
import {
  AVATAR_TONE_SX,
  AvatarTone,
  SCORE_TONE_SX,
  VERB_TONES,
  VerbToken,
  scoreTone,
  verbTokenForVerb,
} from './tones';

export type EvidenceSheetPersona = 'parent' | 'admin';

export interface EvidenceSheetTarget {
  kind: 'channel' | 'video';
  name: string;
  handle?: string | null;
  subs?: string | null;
  /**
   * Real channel/video thumbnail URL from YouTube. When set, the
   * ChannelAvatar renders an actual <img> instead of the letter
   * fallback. Optional — callers may omit when the URL isn't
   * available (older ingestions, channels without avatars cached).
   */
  thumbnailUrl?: string | null;
  /**
   * Visual tone for the avatar. Defaults to the verb's tone if
   * omitted. The design files pre-assign per-target tones (rose for
   * Kolanii, emerald for Veritasium); production callers can leave
   * this null and accept the verb-derived default.
   */
  avatarTone?: AvatarTone | null;
}

export interface EvidenceSheetData {
  verb: SuggestionVerb;
  target: EvidenceSheetTarget;
  evidence: Evidence;
  /**
   * Optional context line shown above the verdict on history /
   * readonly modes — e.g. "Voted 2h ago" or "Ran 11:02 AM · 2 new
   * uploads queued". Ignored in apply mode (which uses the
   * evidence.oneLine verdict instead).
   */
  contextLine?: string | null;
}

/**
 * Sheet mode — drives footer composition. Suggestion lifecycle goes
 *   apply (Pending row)  →  history (Vote row)  →  readonly (Executed row)
 * The body shape is the same; only the footer's CTAs change.
 */
export type EvidenceSheetMode = 'apply' | 'history' | 'readonly';

export interface EvidenceSheetProps {
  data: EvidenceSheetData;
  persona?: EvidenceSheetPersona;
  mode?: EvidenceSheetMode;
  onApply?: () => void;
  onSnooze?: () => void;
  onClose?: () => void;
  /** Wired in history mode. Retracts the parent's vote on this target. */
  onUndoVote?: () => void;
  /**
   * Override the primary button label. Defaults are verb-derived
   * ("Block channel", "Follow channel", "Boost in feed", "Skip").
   */
  primaryActionLabel?: string;
  /**
   * Force the desktop docked layout regardless of breakpoint.
   * Useful in storybook / fixture previews where the caller already
   * provides a sized container.
   */
  forceDocked?: boolean;
  /**
   * Id of the underlying Suggestion. Required to wire the admin
   * layer's "Flag as wrong" button to the flagSuggestionWrong
   * mutation + the suggestion_flagged_wrong telemetry event. Pass
   * null when the sheet is rendering off a fixture (storybook /
   * preview rig).
   */
  suggestionId?: string | null;
  /** Called when admin clicks "Flag this suggestion as wrong". */
  onFlagWrong?: (suggestionId: string, verb: SuggestionVerb) => void;
}

// ─── Local building blocks ─────────────────────────────────────────────────

function VerbPill({verb}: {verb: VerbToken}): React.JSX.Element {
  const t = VERB_TONES[verb];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: `1px solid ${t.pillBorder}`,
        backgroundColor: t.pillBg,
        color: t.pillText,
        borderRadius: '4px',
        px: '6px',
        py: '2px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontSize: '9px',
        lineHeight: 1.2,
      }}
    >
      {t.label}
    </Box>
  );
}

function ChannelAvatar({
  name,
  tone,
  size = 40,
  thumbnailUrl,
  kind = 'channel',
}: {
  name: string;
  tone: AvatarTone;
  size?: number;
  thumbnailUrl?: string | null;
  kind?: 'channel' | 'video';
}): React.JSX.Element {
  const radius = kind === 'video' ? '6px' : '50%';
  if (thumbnailUrl) {
    return (
      <Box
        component="img"
        src={thumbnailUrl}
        alt={name}
        sx={{
          width: size,
          height: size,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          backgroundColor: '#E2E8F0',
        }}
      />
    );
  }
  const initial = name?.[0]?.toUpperCase() ?? '?';
  const sx = AVATAR_TONE_SX[tone];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius,
        background: sx.background,
        color: sx.color,
        width: size,
        height: size,
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initial}
    </Box>
  );
}

function ScoreBadge({score}: {score: number | null | undefined}): React.JSX.Element {
  // Unscored video (cold cache for SCORE_VIDEO or metadata-only verdict)
  // — render a neutral em-dash so the layout doesn't collapse.
  const hasScore = typeof score === 'number' && Number.isFinite(score);
  const tone = hasScore ? SCORE_TONE_SX[scoreTone(score as number)] : SCORE_TONE_SX.unscored;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: `1px solid ${tone.border}`,
        backgroundColor: tone.bg,
        color: tone.color,
        borderRadius: '4px',
        px: '6px',
        fontWeight: 700,
        fontSize: '10px',
        letterSpacing: '0.04em',
        lineHeight: '16px',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {hasScore ? (score as number).toFixed(1) : '—'}
    </Box>
  );
}

function VideoCard({video}: {video: EvidenceVideo}): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid #E2E8F0',
        backgroundColor: '#F8FAFC',
        p: '10px',
      }}
    >
      <Box
        sx={{
          aspectRatio: '16 / 9',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          background: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
          color: '#94A3B8',
          ...(video.thumbnailUrl
            ? {
                backgroundImage: `url(${video.thumbnailUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : null),
        }}
      >
        {!video.thumbnailUrl && <PlayCircleOutline sx={{fontSize: 32}} />}
      </Box>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{mt: 1}}>
        <Typography
          sx={{
            flex: 1,
            color: '#F1F5F9',
            fontSize: '12px',
            lineHeight: 1.35,
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {video.title}
        </Typography>
        <ScoreBadge score={video.score} />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{mt: '6px', flexWrap: 'wrap'}}>
        {video.tags.map(tag => (
          <Box
            key={tag}
            component="span"
            sx={{
              backgroundColor: '#F1F5F9',
              color: '#94A3B8',
              borderRadius: '4px',
              px: '6px',
              py: '2px',
              fontSize: '10px',
            }}
          >
            {tag}
          </Box>
        ))}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          sx={{ml: 'auto', color: '#64748B', fontSize: '10px'}}
        >
          <AccessTimeOutlined sx={{fontSize: 12}} />
          <span>{video.mins}m</span>
        </Stack>
      </Stack>
      {video.watched && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{mt: '6px', color: '#64748B', fontSize: '10px'}}
        >
          <CheckCircleOutline sx={{fontSize: 12}} />
          <span>already watched</span>
        </Stack>
      )}
    </Box>
  );
}

function PreviewRow({kind, text}: {kind: PreviewKind; text: string}): React.JSX.Element {
  const map: Record<PreviewKind, {Icon: typeof InfoOutlined; color: string}> = {
    [PreviewKind.Plus]: {Icon: TrendingUpOutlined, color: '#6EE7B7'},
    [PreviewKind.Minus]: {Icon: TrendingDownOutlined, color: '#FDA4AF'},
    [PreviewKind.Info]: {Icon: InfoOutlined, color: '#94A3B8'},
  };
  const {Icon, color} = map[kind] ?? map[PreviewKind.Info];
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Icon sx={{fontSize: 14, color, flexShrink: 0, mt: '2px'}} />
      <Typography sx={{color: '#334155', fontSize: '12px', lineHeight: 1.5}}>
        {text}
      </Typography>
    </Stack>
  );
}

function FactorBar({factor}: {factor: EvidenceFactor}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 140px auto',
        alignItems: 'center',
        gap: '12px',
        py: '6px',
      }}
    >
      <Typography
        sx={{
          color: '#0F172A',
          fontSize: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {factor.name}
      </Typography>
      <Box
        component="span"
        sx={{
          backgroundColor: '#F1F5F9',
          color: '#94A3B8',
          borderRadius: '4px',
          px: '6px',
          fontWeight: 500,
          fontSize: '10px',
          lineHeight: '16px',
        }}
      >
        ×{factor.weight.toFixed(2)}
      </Box>
      <Box sx={{height: '6px', borderRadius: '999px', backgroundColor: '#E2E8F0'}}>
        <Box
          sx={{
            height: '100%',
            borderRadius: '999px',
            backgroundColor: 'rgba(99, 102, 241, 0.7)',
            width: `${Math.round(factor.score * 100)}%`,
          }}
        />
      </Box>
      <Typography
        sx={{
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: '#0F172A',
          fontSize: '11px',
        }}
      >
        +{factor.contribution.toFixed(3)}
      </Typography>
    </Box>
  );
}

function AdminLayer({
  scoring,
  onFlag,
}: {
  scoring: EvidenceScoring;
  onFlag?: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        backgroundColor: 'rgba(99, 102, 241, 0.04)',
      }}
      data-testid="evidence-sheet-admin-layer"
    >
      <Box
        component="button"
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShieldOutlined sx={{fontSize: 14, color: '#A5B4FC'}} />
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#C7D2FE',
              fontSize: '10px',
              letterSpacing: '0.1em',
            }}
          >
            Admin · Why the model decided this
          </Typography>
        </Stack>
        {open ? (
          <ExpandMoreIcon sx={{fontSize: 16, color: '#A5B4FC'}} />
        ) : (
          <ChevronRightIcon sx={{fontSize: 16, color: '#A5B4FC'}} />
        )}
      </Box>
      <Collapse in={open}>
        <Stack
          spacing={2}
          sx={{borderTop: '1px solid rgba(99, 102, 241, 0.2)', px: 2, py: 2}}
        >
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#94A3B8',
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                }}
              >
                Final score
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{mt: '2px'}}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: '#0F172A',
                    fontSize: '24px',
                  }}
                >
                  {scoring.finalScore.toFixed(2)}
                </Typography>
                <Typography sx={{color: '#64748B', fontSize: '11px'}}>
                  threshold {scoring.threshold.toFixed(2)} ·{' '}
                  {scoring.direction === ThresholdDirection.Below
                    ? 'below → BLOCK'
                    : 'above → FOLLOW'}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{textAlign: 'right'}}>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#94A3B8',
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                }}
              >
                Confidence
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#0F172A',
                  fontSize: '20px',
                }}
              >
                {scoring.confidencePct}%
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Typography
              sx={{
                mb: 0.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#94A3B8',
                fontSize: '10px',
                letterSpacing: '0.1em',
              }}
            >
              Scoring chain
            </Typography>
            {scoring.factors.length > 0 ? (
              <Stack divider={<Box sx={{borderBottom: '1px solid #F1F5F9'}} />}>
                {scoring.factors.map(f => (
                  <FactorBar key={f.name} factor={f} />
                ))}
              </Stack>
            ) : (
              // Per the wiring rules: when factors aren't available
              // yet, render an em-dash in slate rather than fake numbers.
              <Typography sx={{color: '#64748B', fontSize: '12px'}}>—</Typography>
            )}
          </Box>

          {scoring.notes && (
            <Box
              sx={{
                borderRadius: '6px',
                border: '1px solid #E2E8F0',
                backgroundColor: '#F8FAFC',
                p: 1.5,
              }}
            >
              <Typography
                sx={{
                  mb: 0.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#94A3B8',
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                }}
              >
                Model notes
              </Typography>
              <Typography sx={{color: '#334155', fontSize: '12px', lineHeight: 1.5}}>
                {scoring.notes}
              </Typography>
            </Box>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Typography sx={{color: '#64748B', fontSize: '11px'}}>
              {scoring.modelVersion}
            </Typography>
            <Button
              size="small"
              startIcon={<FlagOutlined sx={{fontSize: 14}} />}
              onClick={onFlag}
              disabled={!onFlag}
              sx={{
                border: '1px solid rgba(244, 63, 94, 0.4)',
                backgroundColor: 'rgba(244, 63, 94, 0.1)',
                color: '#FECDD3',
                fontSize: '11px',
                textTransform: 'none',
                '&:hover': {backgroundColor: 'rgba(244, 63, 94, 0.15)'},
              }}
            >
              Flag this suggestion as wrong
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

function EvidenceSheetBody({
  data,
  persona = 'parent',
  mode = 'apply',
  onApply,
  onSnooze,
  onClose,
  onUndoVote,
  primaryActionLabel,
  suggestionId,
  onFlagWrong,
}: EvidenceSheetProps): React.JSX.Element {
  const verbToken = verbTokenForVerb(data.verb);
  const verb = VERB_TONES[verbToken];
  const avatarTone: AvatarTone =
    data.target.avatarTone ??
    (verbToken === 'block'
      ? 'rose'
      : verbToken === 'follow'
        ? 'emerald'
        : verbToken === 'boost'
          ? 'indigo'
          : 'slate');

  const defaultActionLabel =
    verbToken === 'block'
      ? 'Block channel'
      : verbToken === 'follow'
        ? 'Follow channel'
        : verbToken === 'boost'
          ? 'Boost in feed'
          : 'Skip';

  const {evidence} = data;
  // Per the wiring rules: if scoring isn't available, the admin layer
  // renders nothing. Parent never sees the layer regardless.
  const showAdminLayer = persona === 'admin' && evidence.scoring != null;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
      }}
      data-testid="evidence-sheet"
    >
      {/* Header */}
      <Box sx={{borderBottom: '1px solid #E2E8F0', backgroundColor: verb.bgSoft}}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1.5}
          sx={{px: 2.5, pt: 2, pb: 1.5}}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <ChannelAvatar
              name={data.target.name}
              tone={avatarTone}
              size={40}
              thumbnailUrl={data.target.thumbnailUrl}
              kind={data.target.kind}
            />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5}}>
                <VerbPill verb={verbToken} />
                <Typography sx={{color: '#64748B', fontSize: '11px'}}>
                  {data.target.kind}
                </Typography>
              </Stack>
              <Typography
                component="h2"
                sx={{fontWeight: 600, color: '#0F172A', fontSize: '18px', lineHeight: 1.2}}
              >
                {data.target.name}
              </Typography>
              {(data.target.handle || data.target.subs) && (
                <Typography sx={{color: '#94A3B8', fontSize: '11px'}}>
                  {[data.target.handle, data.target.subs ? `${data.target.subs} subs` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
          </Stack>
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close"
              sx={{color: '#94A3B8', '&:hover': {backgroundColor: '#F1F5F9', color: '#0F172A'}}}
            >
              <CloseOutlined sx={{fontSize: 16}} />
            </IconButton>
          )}
        </Stack>
        <Typography sx={{px: 2.5, pb: 2, color: '#334155', fontSize: '13px', lineHeight: 1.55}}>
          {/* History / readonly modes prefer a caller-supplied context
              line (timestamp + outcome) over the model's "if you
              apply…" verdict, which is stale once a vote already
              landed. Falls back to oneLine when no context provided. */}
          {(mode === 'history' || mode === 'readonly') && data.contextLine
            ? data.contextLine
            : evidence.oneLine}
        </Typography>
      </Box>

      {/* Body (scrollable) */}
      <Stack spacing={3} sx={{flex: 1, overflowY: 'auto', px: 2.5, py: 2.5}}>
        {/* Evidence grid — hidden entirely when no videos. Em-dash
            placeholder under an "EVIDENCE · 0 VIDEOS" header read as
            broken/missing rather than intentional. Better to drop the
            section than advertise a hole. */}
        {evidence.videos.length > 0 && (
          <Box component="section">
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: '#64748B',
                fontSize: '10px',
                letterSpacing: '0.1em',
              }}
            >
              Evidence · {evidence.videos.length} videos
            </Typography>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1}}>
              {evidence.videos.map(v => (
                <VideoCard key={v.id} video={v} />
              ))}
            </Box>
          </Box>
        )}

        {/* "What changes if you apply" is only meaningful in apply mode.
            For history / readonly the vote already happened, so the
            forward-looking preview is replaced by the verdict block
            (rendered in the header above) and we skip this section. */}
        {mode === 'apply' && evidence.preview.bullets.length > 0 && (
          <Box
            component="section"
            sx={{
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              backgroundColor: verb.bgSoft,
              p: 2,
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: '#334155',
                fontSize: '10px',
                letterSpacing: '0.1em',
              }}
            >
              {evidence.preview.headline}
            </Typography>
            <Stack spacing={0.75}>
              {evidence.preview.bullets.map((b, i) => (
                <PreviewRow key={i} kind={b.kind} text={b.text} />
              ))}
            </Stack>
            <Typography sx={{mt: 1.5, color: '#64748B', fontSize: '11px'}}>
              {evidence.preview.reversibleNote}
            </Typography>
          </Box>
        )}

        {showAdminLayer && (
          <AdminLayer
            scoring={evidence.scoring as EvidenceScoring}
            onFlag={
              suggestionId && onFlagWrong
                ? () => onFlagWrong(suggestionId, data.verb)
                : undefined
            }
          />
        )}
      </Stack>

      {/* Footer — composition driven by mode. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          borderTop: '1px solid #E2E8F0',
          backgroundColor: '#F8FAFC',
          px: 2.5,
          py: 1.5,
        }}
      >
        {mode === 'apply' ? (
          <>
            <Button
              onClick={onSnooze}
              size="small"
              sx={{color: '#475569', fontSize: '12px', textTransform: 'none', '&:hover': {color: '#0F172A', backgroundColor: '#F1F5F9'}}}
            >
              Snooze 7 days
            </Button>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                onClick={onClose}
                size="small"
                sx={{
                  border: '1px solid rgba(51, 65, 85, 0.8)',
                  backgroundColor: '#F8FAFC',
                  color: '#0F172A',
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {backgroundColor: '#F1F5F9'},
                }}
              >
                Not now
              </Button>
              <Button
                onClick={onApply}
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: verb.primaryBg,
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {backgroundColor: verb.primaryBgHover},
                }}
              >
                {primaryActionLabel ?? defaultActionLabel}
              </Button>
            </Stack>
          </>
        ) : mode === 'history' ? (
          <>
            <Button
              onClick={onUndoVote}
              size="small"
              disabled={!onUndoVote}
              sx={{
                border: '1px solid #FECDD3',
                backgroundColor: '#FFFFFF',
                color: '#BE123C',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {backgroundColor: '#FFF1F2'},
                '&.Mui-disabled': {
                  borderColor: '#E2E8F0',
                  color: '#94A3B8',
                  backgroundColor: '#FFFFFF',
                },
              }}
            >
              Undo this vote
            </Button>
            <Button
              onClick={onClose}
              size="small"
              sx={{
                border: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
                color: '#0F172A',
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {backgroundColor: '#F1F5F9'},
              }}
            >
              Close
            </Button>
          </>
        ) : (
          // readonly — executed actions can't be undone from this surface,
          // only acknowledged. Close-only footer.
          <Box sx={{ml: 'auto'}}>
            <Button
              onClick={onClose}
              size="small"
              sx={{
                border: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
                color: '#0F172A',
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {backgroundColor: '#F1F5F9'},
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export function EvidenceSheet(props: EvidenceSheetProps): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (isMobile && !props.forceDocked) {
    return (
      <Dialog
        open
        fullScreen
        onClose={props.onClose}
        // Dark backdrop on mobile so the sheet floats on the page;
        // the body itself is full-bleed slate.
        PaperProps={{sx: {backgroundColor: '#FFFFFF'}}}
      >
        <EvidenceSheetBody {...props} />
      </Dialog>
    );
  }
  return <EvidenceSheetBody {...props} />;
}
